
import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import { Dashboard } from "@/components/Dashboard";
import { VendedorDashboard } from "@/components/VendedorDashboard";
import { Orcamentos } from "@/components/Orcamentos";
import { Clientes } from "@/components/Clientes";
import { Contratos } from "@/components/Contratos";
import { Financeiro } from "@/components/Financeiro";
import { Sinistros } from "@/components/Sinistros";
import { Agenda } from "@/components/Agenda";
import { Equipamentos } from "@/components/Equipamentos";
import { Servicos } from "@/components/Servicos";
import { Usuarios } from "@/components/Usuarios";
import { Projetos } from "@/components/Projetos";
import { Pedidos } from "@/components/Pedidos";
import { PedidosNaoRenovados } from "@/components/PedidosNaoRenovados";
import { Limpeza } from "@/components/Limpeza";
import { SinistrosNovo } from "@/components/SinistrosNovo";
import { Planos } from "@/components/Planos";
import { StatusCustomizados } from "@/components/StatusCustomizados";
import { Cadastros } from "@/components/Cadastros";
import { CrmModule } from "@/components/crm/CrmModule";
import { TarefasModule } from "@/components/tarefas/TarefasModule";
import { useAuth } from "@/hooks/useAuth";
import { LogOut, Loader2 } from "lucide-react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { getRoleDisplayName, getRoleBadgeColor, isVendedorScopedRole } from "@/types/auth";
import { EditarPerfilDialog } from "@/components/EditarPerfilDialog";

const Index = () => {
  const {
    user,
    profile,
    loading,
    profileError,
    signOut,
    canAccessPedidos,
    canAccessContratos,
    canAccessFinanceiro,
    refreshProfile,
  } = useAuth();
  const [activeSection, setActiveSection] = useState("dashboard");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const renderVendedorOrAdminDashboard = () =>
    isVendedorScopedRole(profile?.role) ? <VendedorDashboard /> : <Dashboard />;

  // Bloquear seções sem permissão (ex.: vendedor externo em pedidos)
  useEffect(() => {
    if (loading || !profile) return;
    const blocked =
      (!canAccessPedidos() &&
        (activeSection === "pedidos" ||
          activeSection === "pedidos_nao_renovados" ||
          activeSection === "limpeza")) ||
      (!canAccessContratos() && activeSection === "contratos") ||
      (!canAccessFinanceiro() && activeSection === "financeiro");
    if (blocked) setActiveSection("dashboard");
  }, [
    loading,
    profile,
    canAccessPedidos,
    canAccessContratos,
    canAccessFinanceiro,
    activeSection,
  ]);

  useEffect(() => {
    console.log('🚀 Index component mounted');
    console.log('👤 User:', user);
    console.log('📋 Profile:', profile);
    console.log('⏳ Loading:', loading);
    console.log('🎯 Active section:', activeSection);
  }, [user, profile, loading, activeSection]);

  // Escutar eventos para mudar seção
  useEffect(() => {
    const handleMudarSecao = (event: CustomEvent) => {
      console.log('🔄 Section change event:', event.detail);
      setActiveSection(event.detail);
    };

    window.addEventListener('mudar_secao', handleMudarSecao as EventListener);
    
    return () => {
      window.removeEventListener('mudar_secao', handleMudarSecao as EventListener);
    };
  }, []);

  if (loading) {
    console.log('⏳ Showing loading spinner');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    console.log('❌ No user, redirecting to auth');
    return <Navigate to="/auth" replace />;
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 bg-gray-50">
        <p className="text-gray-600 text-center">
          Não foi possível carregar seu perfil. Verifique sua conexão e tente novamente.
        </p>
        {profileError && (
          <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded max-w-md text-center">
            {profileError}
          </p>
        )}
        <button
          type="button"
          onClick={() => void refreshProfile()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 font-medium"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  console.log('✅ User authenticated, rendering main content');

  const renderActiveSection = () => {
    console.log('🔍 Rendering section:', activeSection);
    
    switch (activeSection) {
      case "dashboard":
        console.log('📊 Rendering Dashboard component');
        return renderVendedorOrAdminDashboard();
      case "tarefas":
        return (
          <ProtectedRoute roleRequired="viewer">
            <TarefasModule />
          </ProtectedRoute>
        );
      case "crm":
        return (
          <ProtectedRoute roleRequired="viewer">
            <CrmModule />
          </ProtectedRoute>
        );
      case "projetos":
        return (
          <ProtectedRoute roleRequired="viewer">
            <Projetos />
          </ProtectedRoute>
        );
      case "orcamentos":
        return (
          <ProtectedRoute roleRequired="viewer">
            <Orcamentos />
          </ProtectedRoute>
        );
      case "pedidos":
        if (!canAccessPedidos()) {
          return renderVendedorOrAdminDashboard();
        }
        return (
          <ProtectedRoute roleRequired="viewer">
            <Pedidos />
          </ProtectedRoute>
        );
      case "pedidos_nao_renovados":
        if (!canAccessPedidos()) {
          return renderVendedorOrAdminDashboard();
        }
        return (
          <ProtectedRoute roleRequired="viewer">
            <PedidosNaoRenovados />
          </ProtectedRoute>
        );
      case "limpeza":
        if (!canAccessPedidos()) {
          return renderVendedorOrAdminDashboard();
        }
        return (
          <ProtectedRoute roleRequired="viewer">
            <Limpeza />
          </ProtectedRoute>
        );
      case "clientes":
        console.log('🔍 Rendering Clientes component');
        return (
          <ProtectedRoute roleRequired="viewer">
            <Clientes />
          </ProtectedRoute>
        );
      case "contratos":
        if (!canAccessContratos()) {
          return renderVendedorOrAdminDashboard();
        }
        return (
          <ProtectedRoute roleRequired="viewer">
            <Contratos />
          </ProtectedRoute>
        );
      case "financeiro":
        if (!canAccessFinanceiro()) {
          return renderVendedorOrAdminDashboard();
        }
        return (
          <ProtectedRoute roleRequired="viewer">
            <Financeiro />
          </ProtectedRoute>
        );
      case "sinistros":
        return (
          <ProtectedRoute roleRequired="viewer">
            <Sinistros />
          </ProtectedRoute>
        );
      case "agenda":
        return (
          <ProtectedRoute roleRequired="viewer">
            <Agenda />
          </ProtectedRoute>
        );
      case "cadastros":
        return (
          <ProtectedRoute roleRequired="operator">
            <Cadastros />
          </ProtectedRoute>
        );
      case "servicos":
        return (
          <ProtectedRoute roleRequired="admin">
            <Servicos />
          </ProtectedRoute>
        );
      case "usuarios":
        return (
          <ProtectedRoute roleRequired="admin">
            <Usuarios />
          </ProtectedRoute>
        );
      case "equipamentos":
        return (
          <ProtectedRoute roleRequired="viewer">
            <Equipamentos />
          </ProtectedRoute>
        );
      case "planos":
        return (
          <ProtectedRoute roleRequired="viewer">
            <Planos />
          </ProtectedRoute>
        );
      case "status-customizados":
        return (
          <ProtectedRoute roleRequired="viewer">
            <StatusCustomizados />
          </ProtectedRoute>
        );
      default:
        console.log('📊 Rendering Dashboard component (default)');
        return renderVendedorOrAdminDashboard();
    }
  };

  const getUserRoleDisplay = () => {
    if (!profile?.role) return 'Usuário';
    return getRoleDisplayName(profile.role);
  };

  const getUserRoleBadge = () => {
    if (!profile?.role) return 'bg-gray-100 text-gray-800';
    return getRoleBadgeColor(profile.role);
  };

  const handleSignOut = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void signOut();
  };

  return (
    <div className="min-h-screen flex w-full bg-gray-50 relative">
      <AppSidebar 
        activeSection={activeSection} 
        setActiveSection={setActiveSection}
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
      />
      <main
        className={`flex-1 transition-all duration-300 relative z-10 pointer-events-auto ${
          isSidebarCollapsed ? 'ml-20' : 'ml-64'
        }`}
        style={{ minHeight: '100vh' }}
      >
        <header className="bg-white border-b border-gray-200 px-6 py-4 relative z-20 pointer-events-auto">
            <div className="flex items-center justify-end">
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {profile?.nome || user.email}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${getUserRoleBadge()}`}>
                      {getUserRoleDisplay()}
                    </span>
                  </div>
                </div>
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {profile?.nome?.charAt(0) || user.email?.charAt(0) || 'U'}
                  </span>
                </div>
                <EditarPerfilDialog />
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="inline-flex items-center justify-center rounded-md border border-input bg-background px-3 h-9 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                  aria-label="Sair"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </header>
        <div className="p-6 relative z-10 pointer-events-auto">
          {renderActiveSection()}
        </div>
      </main>
    </div>
  );
};

export default Index;
